/**
 * Zod schemas shared between the API (server-side validation) and the frontends
 * (validate-before-queue on the PWA). See doc/09-api-design.md §10.
 */
import { z } from 'zod';
import { AssetType, CheckpointType, LocationType, UserRole, } from './types.js';
export const assetTypeSchema = z.enum(AssetType);
export const checkpointTypeSchema = z.enum(CheckpointType);
export const locationTypeSchema = z.enum(LocationType);
export const userRoleSchema = z.enum(UserRole);
// --- Auth ---
export const loginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(1),
});
// --- Mould ---
// Optional cuid FK, accepting empty string / null from form selects (→ undefined).
const optId = z
    .string()
    .optional()
    .nullable()
    .transform((v) => (v ? v : undefined));
export const createMouldSchema = z.object({
    // assetCode is optional: when omitted the server auto-allocates it from the
    // chosen project (projectCode + next 3-digit part sequence). See §2.
    assetCode: z.string().min(1).max(64).optional(),
    name: z.string().min(1),
    description: z.string().optional().nullable(),
    type: assetTypeSchema.optional().default('MOULD'),
    ownerDepartment: z.string().optional().nullable(),
    purchaseDate: z.string().datetime().optional().nullable(),
    photoUrl: z.string().optional().nullable(),
    // --- v2 catalog scalar fields ---
    catalogNo: z.coerce.number().int().optional().nullable(),
    cavities: z.string().optional().nullable(),
    moldSize: z.string().optional().nullable(),
    // A year, nothing finer: the plate and the client's sheets both carry the year alone.
    manufactureYear: z.coerce.number().int().min(1950, 'A year from 1950 on').max(2100, 'A four-digit year').optional().nullable(),
    manufactureDate: z.string().datetime().optional().nullable(),
    qrNumber: z.string().max(64).optional().nullable(),
    toolTransfer: z.boolean().optional().nullable(),
    // --- v2 catalog relations (master FK ids) ---
    projectId: optId,
    ownerCompanyId: optId,
    customerCompanyId: optId,
    manufactureCompanyId: optId,
    designerId: optId,
    toolTypeId: optId,
    runnerTypeId: optId,
    gateTypeId: optId,
    ejectionTypeId: optId,
    coreCavityMaterialId: optId,
    moldBaseMaterialId: optId,
    materialGradeId: optId,
    mechanismTypeId: optId,
    nameplateTemplateId: optId,
});
// status is derived and never accepted here (a PATCH with status -> 422)
export const updateMouldSchema = createMouldSchema.partial().extend({
    isActive: z.boolean().optional(),
});
// --- Master data ---
export const createLocationSchema = z.object({
    code: z.string().min(1),
    name: z.string().min(1),
    type: locationTypeSchema,
    address: z.string().optional().nullable(),
});
export const createStoreSchema = z.object({
    locationId: z.string().min(1),
    code: z.string().min(1),
    name: z.string().min(1),
});
export const createCheckpointSchema = z.object({
    code: z.string().min(1),
    name: z.string().min(1),
    locationId: z.string().min(1),
    storeId: z.string().optional().nullable(),
    type: checkpointTypeSchema,
});
export const updateLocationSchema = createLocationSchema.partial();
export const updateStoreSchema = createStoreSchema.partial();
export const updateCheckpointSchema = createCheckpointSchema.partial();
// --- v2 spec-catalog master tables ---
// Every master has code + name + isActive. Some add a few extra fields.
const optNullId = z.string().optional().nullable().transform((v) => (v ? v : undefined));
export const createMasterSchema = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1),
    isActive: z.boolean().optional().default(true),
});
export const updateMasterSchema = createMasterSchema.partial();
export const createCompanySchema = createMasterSchema.extend({
    logoSvg: z.string().optional().nullable(),
});
export const updateCompanySchema = createCompanySchema.partial();
export const createProjectSchema = createMasterSchema.extend({
    ownerCompanyId: optNullId,
});
export const updateProjectSchema = createProjectSchema.partial();
export const createMaterialGradeSchema = createMasterSchema.extend({
    shrinkagePct: z.coerce.number().optional().nullable(),
});
export const updateMaterialGradeSchema = createMaterialGradeSchema.partial();
export const createNameplateTemplateSchema = createMasterSchema.extend({
    plateW: z.coerce.number().positive().default(90),
    plateH: z.coerce.number().positive().default(55),
    cornerRadius: z.coerce.number().min(0).default(4),
    holeDiameter: z.coerce.number().min(0).default(5),
    holePositions: z.array(z.object({ x: z.number(), y: z.number() })).optional().nullable(),
    qrBox: z
        .object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() })
        .optional()
        .nullable(),
    headerCompanyRef: z.enum(['owner', 'manufacturer']).default('manufacturer'),
    fieldLayout: z
        .array(z.object({ label: z.string(), token: z.string(), y: z.number().optional() }))
        .optional()
        .nullable(),
});
export const updateNameplateTemplateSchema = createNameplateTemplateSchema.partial();
// --- Scan event (the core payload) ---
export const scanEventSchema = z.object({
    clientScanId: z.string().uuid(),
    assetCode: z.string().min(1),
    // optional: server uses the user's single assigned checkpoint when omitted
    checkpointCode: z.string().min(1).optional(),
    scannedAt: z.string().datetime(),
    gpsLat: z.number().min(-90).max(90).optional(),
    gpsLng: z.number().min(-180).max(180).optional(),
    remarks: z.string().optional(),
    photoUrl: z.string().optional(),
    /**
     * When the person expects to bring it back. Only meaningful on a check-out;
     * the server ignores it at any other checkpoint type, because a return date
     * on a QC waypoint would mean nothing.
     */
    expectedBackBy: z.string().datetime().nullish(),
});
export const scanBatchSchema = z.object({
    scans: z.array(scanEventSchema).min(1).max(100),
});
// --- Transfer ---
export const createTransferSchema = z.object({
    mouldId: z.string().min(1),
    fromLocationId: z.string().min(1),
    fromStoreId: z.string().optional().nullable(),
    toLocationId: z.string().min(1),
    toStoreId: z.string().optional().nullable(),
    expectedRoute: z.array(z.string()).default([]),
    expectedArrivalAt: z.string().datetime().optional().nullable(),
});
export const updateTransferSchema = z.object({
    /**
     * `dispatch` moves a DRAFT onto the road. It is separate from `complete`
     * because raising the paperwork and the tool actually leaving are different
     * moments, often hours apart, and the gate pass is printed between them.
     */
    action: z.enum(['dispatch', 'cancel', 'complete']),
    remarks: z.string().optional(),
});
// --- Journeys ---
export const journeyStopKindSchema = z.enum(['START', 'CHECKPOINT', 'DESTINATION']);
const stopInput = z.object({
    locationId: z.string().min(1),
    kind: journeyStopKindSchema,
});
/**
 * A reusable plan - "Mira Road to Delhi via Vasai" - typed once and started
 * many times.
 *
 * The stops are validated for shape here and for sense in `validateStops`: zod
 * can say there are at least two of them, but not that the first is a start and
 * the last a destination, and those rules belong with the journey logic where
 * they can be argued with in a test.
 */
export const createRouteSchema = z.object({
    code: z.string().min(1).max(32),
    name: z.string().min(1).max(120),
    stops: z.array(stopInput).min(2).max(20),
});
export const updateRouteSchema = createRouteSchema.partial().extend({
    stops: z.array(stopInput).min(2).max(20).optional(),
});
/**
 * One tool actually making a trip.
 *
 * Either from a route, or with stops given directly for a one-off - most trips
 * repeat, but the one that does not should not need a saved plan nobody will
 * use again.
 */
export const createJourneySchema = z
    .object({
    mouldId: z.string().min(1),
    routeId: z.string().min(1).optional(),
    stops: z.array(stopInput).min(2).max(20).optional(),
    /** Start it straight away rather than leaving it planned. */
    start: z.boolean().default(false),
})
    .refine((v) => (v.routeId == null) !== (v.stops == null), {
    message: 'Give either a route or a list of stops, not both.',
    path: ['routeId'],
});
export const updateJourneySchema = z.object({
    action: z.enum(['start', 'cancel']),
});
// --- Users (admin) ---
export const createUserSchema = z.object({
    name: z.string().min(1),
    email: z.string().email(),
    password: z.string().min(8),
    role: userRoleSchema,
    assignedCheckpointIds: z.array(z.string()).default([]),
    assignedLocationId: z.string().optional().nullable(),
});
export const updateUserSchema = z.object({
    name: z.string().min(1).optional(),
    role: userRoleSchema.optional(),
    assignedCheckpointIds: z.array(z.string()).optional(),
    assignedLocationId: z.string().optional().nullable(),
    isActive: z.boolean().optional(),
    /**
     * An administrator setting someone else's password - the "they forgot it and
     * there is no reset mail" path. Deliberately not paired with a current
     * password: the admin does not know it, which is the whole point.
     */
    password: z.string().min(8).optional(),
});
/**
 * Changing your own password. The current one is required and checked, so
 * knowing a signed-in session is not enough to take the account over - an
 * unattended screen would otherwise be a full account handover.
 */
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: z.string().min(8, 'New password must be at least 8 characters'),
});
/**
 * Edit your own profile.
 *
 * Name only. Email is the sign-in identifier and role is the permission grant -
 * letting someone change either on themselves would turn a profile screen into
 * a privilege-escalation route, so both stay with an administrator.
 */
export const updateProfileSchema = z.object({
    name: z.string().trim().min(1, 'Name cannot be empty').max(120),
});
// --- Pagination query ---
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
// --- Maintenance and usage (v4) ---
export const maintenanceTypeSchema = z.enum([
    'PREVENTIVE',
    'BREAKDOWN',
    'MODIFICATION',
    'REFURBISHMENT',
    'INSPECTION',
]);
export const maintenanceStatusSchema = z.enum([
    'OPEN',
    'IN_PROGRESS',
    'ON_HOLD',
    'COMPLETED',
    'CANCELLED',
]);
export const createJobSchema = z.object({
    mouldId: z.string().min(1),
    type: maintenanceTypeSchema.default('PREVENTIVE'),
    summary: z.string().min(1, 'Say what the job is'),
    details: z.string().optional().nullable(),
    assignedToUserId: z.string().optional().nullable(),
    failureReasonId: z.string().optional().nullable(),
    dueAt: z.string().datetime().optional().nullable(),
    scheduleId: z.string().optional().nullable(),
});
export const updateJobSchema = z.object({
    status: maintenanceStatusSchema.optional(),
    type: maintenanceTypeSchema.optional(),
    summary: z.string().min(1).optional(),
    details: z.string().optional().nullable(),
    assignedToUserId: z.string().optional().nullable(),
    failureReasonId: z.string().optional().nullable(),
    dueAt: z.string().datetime().optional().nullable(),
    downtimeHours: z.number().min(0).optional().nullable(),
    labourCost: z.number().min(0).optional().nullable(),
    partsCost: z.number().min(0).optional().nullable(),
    shotsAtService: z.number().int().min(0).optional().nullable(),
});
export const createScheduleSchema = z
    .object({
    mouldId: z.string().min(1),
    type: maintenanceTypeSchema.default('PREVENTIVE'),
    summary: z.string().min(1, 'Say what the service is'),
    everyDays: z.number().int().positive().optional().nullable(),
    everyShots: z.number().int().positive().optional().nullable(),
})
    // A schedule with neither trigger can never come due, which makes it a row
    // that looks like a plan and is not one.
    .refine((v) => v.everyDays != null || v.everyShots != null, {
    message: 'Set a day interval, a shot interval, or both',
    path: ['everyDays'],
});
export const createShotReadingSchema = z.object({
    mouldId: z.string().min(1),
    reading: z.number().int().min(0),
    readAt: z.string().datetime().optional().nullable(),
    source: z.enum(['manual', 'scan', 'import']).default('manual'),
    note: z.string().optional().nullable(),
});
//# sourceMappingURL=schemas.js.map